import { Col, Form, Input, Modal, Row } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useCreateAffiliatePartner, useUpdateAffiliatePartner } from "../../../../common/api/queries";
import HiddenInput from "../../../../common/components/form/components/HiddenInput";
import { ModalSizes, rowGutter } from "../../../../common/constants";
import { setErrorsToForm } from "../../../../common/utils/formUtils";
import { validations } from "../../../../common/utils/validationUtils";
import { AffiliatePartner, CreateUpdateAffiliatePartner } from "../../types";
import { convertAffiliatePartnerToCreateUpdateObject } from "../../utils";

interface Props {
  affiliatePartner?: AffiliatePartner;
  open: boolean;
  onFormCancel: () => void;
}

export const AffiliatePartnerModalForm = ({ affiliatePartner, open, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<CreateUpdateAffiliatePartner>();

  const createAffiliatePartner = useCreateAffiliatePartner();
  const updateAffiliatePartner = useUpdateAffiliatePartner();

  useEffect(() => {
    if (open && affiliatePartner) {
      const formData = convertAffiliatePartnerToCreateUpdateObject(affiliatePartner);

      form.setFieldsValue(formData);
    }
  }, [open, affiliatePartner, form]);

  const handleFormSubmit = () => {
    form.validateFields().then((values) => {
      if (affiliatePartner) {
        updateAffiliatePartner.mutate(
          { id: affiliatePartner.id, object: values },
          {
            onSuccess: onFormCancel,
            onError: (error) => {
              setErrorsToForm(form, "affiliatePartner.attrs", error.violations);
            },
          },
        );
      } else {
        createAffiliatePartner.mutate(values, {
          onSuccess: onFormCancel,
          onError: (error) => {
            setErrorsToForm(form, "affiliatePartner.attrs", error.violations);
          },
        });
      }
    });
  };

  return (
    <Modal
      width={ModalSizes.TINY}
      open={open}
      title={affiliatePartner ? t("affiliatePartner.titles.updatePartner") : t("affiliatePartner.titles.createPartner")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={createAffiliatePartner.isPending || updateAffiliatePartner.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="affiliatePartnerForm">
        <HiddenInput name="optimisticLockVersion" />

        <Row gutter={rowGutter}>
          <Col span={24}>
            <Form.Item
              name="name"
              label={t("affiliatePartner.attrs.name")}
              rules={[validations.notBlank, validations.size(1, 64)]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
